import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectIsAudioEnabled } from '../../../app/actions/audio/audioSlice';
import { MediaItem } from '../../../app/services/mediaItems';
import {
  MaClip,
  MaSegment,
  MaVideoSpan,
  dbToGain,
  effectiveClipDurationSeconds,
  maSegmentDisplaySeconds,
  videoSpanIndexAt,
} from './maTypes';

/**
 * MA スライドの FE ミックス再生エンジン。
 *
 * クロック設計（ハイブリッド）:
 * - 動画区間（表示時刻 < 動画表示尺）: video 要素がマスタークロック。
 *   オーディオクリップは AudioContext にスケジュールし、rAF で drift を監視、
 *   閾値超過で再スケジュールする。video の waiting / seeking では音を即停止して
 *   先行を防ぎ、playing / seeked で再スケジュールする。
 * - テール区間（動画終了後もクリップが残る場合）: AudioContext クロックに
 *   引き継ぎ、video は最終フレームで停止したままオーディオを最後まで再生する。
 *   ファイルが表示尺より先に尽きた場合も、時刻を飛ばさず実位置からテールへ移る。
 *   video の終端は 'ended' で検知するため、video 要素はループさせない（MaSlide）。
 *
 * 動画表示区間（videoSpans）:
 * セグメントの動画は区間リスト（オプション候補の動画はスロット位置から先の
 * 区間になる）。表示時刻 t とファイル時刻の関係は現在区間 span に対して
 *   videoFileTime = span.fileOffsetSeconds + (t - span.startSeconds)
 * 区間境界に達したら、同一ファイルならシーク、別ファイルなら
 * displayVideoItem（MaSlide の src）を差し替え、loadedmetadata で位置を
 * 合わせてから再開する（pendingSpanSwitch）。
 *
 * デコードは original_url（WAV 等の原本 = AAC エンコーダディレイなし）を優先し、
 * 失敗時に optimized_url（m4a）へフォールバックする。
 * バッファはスライドがアクティブな間のみ保持し、非アクティブ化で解放する。
 */

/** ソース開始を少し先に置いてスケジュールの整列を安定させる */
const SCHEDULE_AHEAD_SECONDS = 0.05;
/** video ↔ AudioContext クロックの許容 drift（超えたら再スケジュール） */
const DRIFT_THRESHOLD_SECONDS = 0.06;
/** 終端・境界判定のイプシロン */
const EDGE_EPSILON = 0.02;
/** デコード対象の最大ファイルサイズ（デコード後の PCM メモリを考慮） */
const MAX_DECODE_BYTES = 200 * 1024 * 1024;

/**
 * 区間開始が動画表示尺の内側にあるか（境界切替・次区間送りの対象か）。
 * videoLen 未確定（<= 0）は内側とみなす
 */
function spanStartsWithinDisplay(startSeconds: number, videoLen: number): boolean {
  return videoLen <= 0 || startSeconds < videoLen - EDGE_EPSILON;
}

/** テール区間で最終フレーム表示に使う区間 index（表示尺末尾が属する区間） */
function finalSpanIndex(spans: MaVideoSpan[], videoLen: number): number {
  if (videoLen <= 0) return spans.length - 1;
  return videoSpanIndexAt(spans, Math.max(0, videoLen - EDGE_EPSILON));
}

// ===== 共有 AudioContext（ブラウザのコンテキスト数上限を避けるためシングルトン） =====
let sharedAudioContext: AudioContext | null = null;

export function getSharedAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!sharedAudioContext) {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    sharedAudioContext = new Ctor();
  }
  return sharedAudioContext;
}

/**
 * メディアアイテム音声のデコード（original_url 優先 → optimized_url フォールバック）。
 * 動画アイテムの場合はコンテナから音声トラックがデコードされる。
 * 再生エンジンとレンダリング書き出し（renderMaSegment）で共用。
 */
export async function decodeMediaItemAudio(
  item: Pick<MediaItem, 'original_url' | 'optimized_url'>,
  ctx: BaseAudioContext,
): Promise<AudioBuffer> {
  const urls = [item.original_url, item.optimized_url].filter((u): u is string => !!u);
  let lastError: unknown = new Error('no decodable url');
  for (const url of urls) {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
      const buffer = await res.arrayBuffer();
      if (buffer.byteLength > MAX_DECODE_BYTES) throw new Error('file too large to decode');
      return await ctx.decodeAudioData(buffer);
    } catch (e) {
      lastError = e;
    }
  }
  throw lastError;
}

export interface MaEngineHandle {
  play: () => Promise<void>;
  pause: () => void;
  seekTo: (_seconds: number) => void;
  toggle: () => void;
  getCurrentTime: () => number;
  getDuration: () => number;
}

export function useMaAudioEngine(params: {
  segment: MaSegment;
  /** MaSlide がマウント後に解決した video 要素（未解決時 null） */
  videoEl: HTMLVideoElement | null;
  /** スライドがアクティブ（表示中バリエーション）か。false でバッファ解放 */
  active: boolean;
  /** 再生状態の変更通知（Preview 側の playingStates 管理用） */
  onPlayStateChange?: (_playing: boolean) => void;
}): {
  handle: MaEngineHandle;
  /** 現在表示すべき動画（現在区間の videoItem）。MaSlide の src / メタ表示に使う */
  displayVideoItem: MediaItem;
} {
  const { segment, videoEl, active } = params;

  const segmentRef = useRef(segment);
  segmentRef.current = segment;
  const videoElRef = useRef(videoEl);
  videoElRef.current = videoEl;
  const onPlayStateChangeRef = useRef(params.onPlayStateChange);
  onPlayStateChangeRef.current = params.onPlayStateChange;

  const isPlayingRef = useRef(false);
  /** 'video': 動画区間（video がマスター） / 'tail': 動画終了後の音のみ区間 */
  const modeRef = useRef<'video' | 'tail'>('video');
  /** 停止中の表示時刻（テール中の getCurrentTime / 再開位置） */
  const pausedAtRef = useRef(0);
  /** AudioContext クロック ↔ 表示時刻のアンカー（スケジュール時に設定） */
  const anchorRef = useRef<{ ctxTime: number; mediaTime: number } | null>(null);
  const masterGainRef = useRef<GainNode | null>(null);
  const buffersRef = useRef(new Map<string, AudioBuffer | 'loading' | 'error'>());
  /** バッファ解放時にインクリメントし、解決済み Promise を無効化する */
  const generationRef = useRef(0);
  const activeSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const rafRef = useRef(0);
  /** エンジン内部から video.pause() した際に 'pause' ハンドラをスキップするフラグ */
  const internalVideoPauseRef = useRef(false);

  // ===== 動画表示区間（videoSpans）の状態 =====
  /** 現在区間 index（MaSlide の src と対応。state は再レンダー用、ref は同期参照用） */
  const [currentSpanIndex, setCurrentSpanIndex] = useState(0);
  const spanIndexRef = useRef(0);
  /**
   * 別ファイルへの区間切替の進行状態。src 差し替え（MaSlide 再レンダー）後の
   * loadedmetadata で displayTime に対応するファイル時刻へシークし、
   * resumePlay なら再生を再開する。切替中の表示時刻はこの値を使う
   */
  const pendingSpanSwitchRef = useRef<{ displayTime: number; resumePlay: boolean } | null>(null);

  /** 現在区間（spanIndexRef を区間数にクランプ） */
  const getCurrentSpan = useCallback((): MaVideoSpan => {
    const spans = segmentRef.current.videoSpans;
    return spans[Math.min(spanIndexRef.current, spans.length - 1)];
  }, []);

  // ===== グローバルオーディオトグル（audioSlice）への追従 =====
  const isAudioEnabled = useSelector(selectIsAudioEnabled) as boolean;
  const audioEnabledRef = useRef(isAudioEnabled);
  audioEnabledRef.current = isAudioEnabled;
  useEffect(() => {
    if (masterGainRef.current) {
      masterGainRef.current.gain.value = isAudioEnabled ? 1 : 0;
    }
  }, [isAudioEnabled]);

  // ===== 基本ヘルパー（全て ref ベースで identity 安定） =====

  const ensureCtx = useCallback((): AudioContext | null => {
    const ctx = getSharedAudioContext();
    if (ctx && !masterGainRef.current) {
      const gain = ctx.createGain();
      gain.gain.value = audioEnabledRef.current ? 1 : 0;
      gain.connect(ctx.destination);
      masterGainRef.current = gain;
    }
    return ctx;
  }, []);

  /** 動画の表示尺（CSV 指定（候補による延長込み） > item.duration > video 要素の実尺） */
  const getVideoLen = useCallback((): number => {
    const seg = segmentRef.current;
    const displaySeconds = maSegmentDisplaySeconds(seg);
    if (displaySeconds != null) return displaySeconds;
    // 表示尺未解析時のフォールバック: ロード中ファイルの実尺を現在区間で表示時刻へ換算
    const el = videoElRef.current;
    const duration = el && Number.isFinite(el.duration) ? el.duration : 0;
    const span = getCurrentSpan();
    return Math.max(0, span.startSeconds + (duration - span.fileOffsetSeconds));
  }, [getCurrentSpan]);

  /** クリップの実効長（トリム・後着優先カットを反映。未デコードなら 0 扱い） */
  const getClipDuration = useCallback((clip: MaClip): number => {
    const buf = buffersRef.current.get(clip.audioItem.id);
    const fallback = buf instanceof AudioBuffer ? buf.duration : 0;
    return effectiveClipDurationSeconds(clip, fallback);
  }, []);

  const getClipsEnd = useCallback((): number => {
    return segmentRef.current.clips.reduce(
      (max, clip) => Math.max(max, clip.startSeconds + getClipDuration(clip)),
      0,
    );
  }, [getClipDuration]);

  const getDuration = useCallback(
    (): number => Math.max(getVideoLen(), getClipsEnd()),
    [getVideoLen, getClipsEnd],
  );

  /** 動画区間の表示時刻（現在区間のマッピング。区間切替中は目標時刻） */
  const getVideoDisplayTime = useCallback((): number => {
    const pending = pendingSpanSwitchRef.current;
    if (pending) return pending.displayTime;
    const el = videoElRef.current;
    if (!el) return pausedAtRef.current;
    const span = getCurrentSpan();
    return Math.max(0, span.startSeconds + (el.currentTime - span.fileOffsetSeconds));
  }, [getCurrentSpan]);

  const getCurrentTime = useCallback((): number => {
    if (modeRef.current === 'video') {
      return getVideoDisplayTime();
    }
    if (isPlayingRef.current && anchorRef.current) {
      const ctx = getSharedAudioContext();
      if (ctx) {
        return anchorRef.current.mediaTime + (ctx.currentTime - anchorRef.current.ctxTime);
      }
    }
    return pausedAtRef.current;
  }, [getVideoDisplayTime]);

  const stopSources = useCallback(() => {
    activeSourcesRef.current.forEach(source => {
      try {
        source.onended = null;
        source.stop();
      } catch {
        /* 既に停止済み */
      }
      try {
        source.disconnect();
      } catch {
        /* noop */
      }
    });
    activeSourcesRef.current = [];
  }, []);

  const stopRaf = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
  }, []);

  const setPlaying = useCallback((playing: boolean) => {
    if (isPlayingRef.current === playing) return;
    isPlayingRef.current = playing;
    onPlayStateChangeRef.current?.(playing);
  }, []);

  // ===== スケジューリング =====

  /**
   * クリップ 1 本をアンカー（ctxTime ↔ mediaTime 対応点）基準でスケジュールする。
   * アンカーより過去に始まるクリップは途中から、未来のクリップは予約起動。
   */
  const scheduleClip = useCallback(
    (clip: MaClip, buffer: AudioBuffer, anchorCtxTime: number, anchorMediaTime: number) => {
      const ctx = getSharedAudioContext();
      const master = masterGainRef.current;
      if (!ctx || !master) return;

      // 実効長 = 実尺と後着優先カット（truncateAtSeconds）の小さい方
      const clipDur = effectiveClipDurationSeconds(clip, buffer.duration);
      if (clipDur <= 0) return; // 同時開始カットで全消しされたクリップ
      const clipEnd = clip.startSeconds + clipDur;
      if (clipEnd <= anchorMediaTime + 0.005) return; // 既に終わっている

      const offsetInClip = Math.max(0, anchorMediaTime - clip.startSeconds);
      const when = anchorCtxTime + Math.max(0, clip.startSeconds - anchorMediaTime);

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      const gain = ctx.createGain();
      gain.gain.value = dbToGain(clip.gainDb);
      source.connect(gain);
      gain.connect(master);
      source.onended = () => {
        activeSourcesRef.current = activeSourcesRef.current.filter(s => s !== source);
        try {
          source.disconnect();
        } catch {
          /* noop */
        }
      };
      source.start(Math.max(when, ctx.currentTime), offsetInClip, Math.max(0.001, clipDur - offsetInClip));
      activeSourcesRef.current.push(source);
    },
    [],
  );

  /** デコード完了が再生中に届いたクリップを現在位置から途中参加させる */
  const scheduleClipLive = useCallback(
    (clip: MaClip, buffer: AudioBuffer) => {
      const ctx = getSharedAudioContext();
      if (!ctx || !isPlayingRef.current) return;
      scheduleClip(clip, buffer, ctx.currentTime, getCurrentTime());
    },
    [scheduleClip, getCurrentTime],
  );

  /** クリップのデコードを開始（未取得のもののみ）。完了時、再生中なら途中スケジュール */
  const ensureBuffer = useCallback(
    (clip: MaClip) => {
      const id = clip.audioItem.id;
      if (buffersRef.current.has(id)) return;
      const ctx = ensureCtx();
      if (!ctx) return;
      buffersRef.current.set(id, 'loading');
      const generation = generationRef.current;
      decodeMediaItemAudio(clip.audioItem, ctx)
        .then(buffer => {
          if (generationRef.current !== generation) return;
          buffersRef.current.set(id, buffer);
          scheduleClipLive(clip, buffer);
        })
        .catch(e => {
          if (generationRef.current !== generation) return;
          console.warn(`[MA] オーディオのデコードに失敗: ${clip.audioItem.file_name}`, e);
          buffersRef.current.set(id, 'error');
        });
    },
    [ensureCtx, scheduleClipLive],
  );

  /** 表示時刻 mediaTime から全クリップを再スケジュール（アンカーも更新） */
  const scheduleAllFrom = useCallback(
    (mediaTime: number) => {
      const ctx = ensureCtx();
      if (!ctx) return;
      stopSources();
      const anchorCtxTime = ctx.currentTime + SCHEDULE_AHEAD_SECONDS;
      anchorRef.current = { ctxTime: anchorCtxTime, mediaTime };
      segmentRef.current.clips.forEach(clip => {
        const buf = buffersRef.current.get(clip.audioItem.id);
        if (buf instanceof AudioBuffer) {
          scheduleClip(clip, buf, anchorCtxTime, mediaTime);
        } else if (buf !== 'error') {
          ensureBuffer(clip);
        }
      });
    },
    [ensureCtx, stopSources, scheduleClip, ensureBuffer],
  );

  // ===== 動画区間の切替 =====

  /**
   * 表示を targetIndex の区間へ切り替え、表示時刻 displayTime に対応する
   * ファイル時刻へ位置を合わせる。
   *
   * - 同一ファイル（現在ロード中 = 切替先）: その場でシークする
   *   （'seeked' でオーディオが再スケジュールされる）
   * - 別ファイル: pendingSpanSwitch を立てる。currentSpanIndex の更新で
   *   MaSlide の src が差し替わり、loadedmetadata で位置合わせ +
   *   resumePlay なら再生再開する
   */
  const switchToSpan = useCallback(
    (targetIndex: number, displayTime: number, resumePlay: boolean, loadedVideoId?: string) => {
      const spans = segmentRef.current.videoSpans;
      const clamped = Math.max(0, Math.min(targetIndex, spans.length - 1));
      const span = spans[clamped];
      const fromId = loadedVideoId ?? getCurrentSpan().videoItem.id;
      spanIndexRef.current = clamped;
      setCurrentSpanIndex(clamped);
      stopSources();
      pausedAtRef.current = displayTime;

      // 切替が進行中なら、ロード済みファイルが不定のため常に pending を更新する
      if (pendingSpanSwitchRef.current || span.videoItem.id !== fromId) {
        pendingSpanSwitchRef.current = { displayTime, resumePlay };
        return;
      }
      const el = videoElRef.current;
      if (!el) return;
      const fileTime = Math.max(0, span.fileOffsetSeconds + (displayTime - span.startSeconds));
      const target = Number.isFinite(el.duration) ? Math.min(el.duration, fileTime) : fileTime;
      if (Math.abs(el.currentTime - target) > 0.01) {
        el.currentTime = target; // 'seeked' で再スケジュールされる
      }
      if (resumePlay && el.paused) void el.play().catch(() => {});
    },
    [getCurrentSpan, stopSources],
  );

  // ===== 再生終了 / テール遷移 =====

  const finishPlayback = useCallback(
    (atSeconds: number) => {
      stopSources();
      pausedAtRef.current = atSeconds;
      setPlaying(false);
      stopRaf();
    },
    [stopSources, setPlaying, stopRaf],
  );

  /** 動画区間からテール区間へ移行（スケジュール済みソースはそのまま鳴らし続ける） */
  const enterTail = useCallback((fromSeconds: number) => {
    if (modeRef.current === 'tail') return;
    modeRef.current = 'tail';
    const el = videoElRef.current;
    if (el && !el.paused) {
      internalVideoPauseRef.current = true;
      el.pause();
    }
    const ctx = getSharedAudioContext();
    if (ctx) anchorRef.current = { ctxTime: ctx.currentTime, mediaTime: fromSeconds };
    pausedAtRef.current = fromSeconds;
  }, []);

  /** テール区間の再生（再開）。video は末尾停止のまま AudioContext クロックで進行 */
  const resumeTail = useCallback(
    (fromSeconds: number) => {
      const ctx = ensureCtx();
      if (ctx?.state === 'suspended') void ctx.resume().catch(() => {});
      modeRef.current = 'tail';
      pausedAtRef.current = fromSeconds;
      setPlaying(true);
      scheduleAllFrom(fromSeconds);
      startRafRef.current();
    },
    [ensureCtx, setPlaying, scheduleAllFrom],
  );

  // ===== rAF ループ（区間境界 / drift 補正 + テール遷移 / 終端検知） =====

  const tick = useCallback(() => {
    if (!isPlayingRef.current) return;
    const ctx = getSharedAudioContext();
    const el = videoElRef.current;

    if (modeRef.current === 'video') {
      // 区間切替（src 差し替え）中はロード完了（loadedmetadata）を待つ
      if (pendingSpanSwitchRef.current) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const videoLen = getVideoLen();
      const spans = segmentRef.current.videoSpans;
      const spanIdx = Math.min(spanIndexRef.current, spans.length - 1);
      const t = getVideoDisplayTime();

      // 次区間の開始に達したら切替（表示尺の内側にある境界のみ）
      const targetIdx = videoSpanIndexAt(spans, t);
      if (targetIdx > spanIdx && spanStartsWithinDisplay(spans[targetIdx].startSeconds, videoLen)) {
        switchToSpan(targetIdx, t, !!el && !el.paused);
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const atVideoEnd = (videoLen > 0 && t >= videoLen - EDGE_EPSILON) || !!(el && el.ended);

      if (atVideoEnd) {
        // ファイルが区間より先に尽きた（候補動画が短い等）場合は次区間の頭へ
        const nextSpan = spans[spanIdx + 1];
        if (el && el.ended && nextSpan && spanStartsWithinDisplay(nextSpan.startSeconds, videoLen)) {
          switchToSpan(spanIdx + 1, nextSpan.startSeconds, true);
          rafRef.current = requestAnimationFrame(tick);
          return;
        }
        // 表示尺より手前でファイルが尽きた場合も時刻は飛ばさず、実位置から
        // 最終フレーム停止のまま AudioContext クロックで表示尺・テールまで進める
        if (Math.max(getClipsEnd(), videoLen) > t + EDGE_EPSILON) {
          enterTail(t);
        } else {
          if (el && !el.paused) {
            internalVideoPauseRef.current = true;
            el.pause();
          }
          finishPlayback(videoLen);
          return;
        }
      } else if (ctx && anchorRef.current && el && !el.paused && !el.seeking) {
        // drift 補正: AudioContext クロック上の期待位置と video 実位置の乖離を監視
        const expected =
          anchorRef.current.mediaTime + (ctx.currentTime - anchorRef.current.ctxTime);
        if (Math.abs(expected - t) > DRIFT_THRESHOLD_SECONDS) {
          scheduleAllFrom(t);
        }
      }
    } else {
      // テール区間: AudioContext クロックで終端を監視
      const total = getDuration();
      if (total > 0 && getCurrentTime() >= total - EDGE_EPSILON) {
        finishPlayback(total);
        return;
      }
    }

    rafRef.current = requestAnimationFrame(tick);
  }, [
    getVideoLen,
    getVideoDisplayTime,
    getClipsEnd,
    getDuration,
    getCurrentTime,
    switchToSpan,
    enterTail,
    finishPlayback,
    scheduleAllFrom,
  ]);

  const startRaf = useCallback(() => {
    stopRaf();
    rafRef.current = requestAnimationFrame(tick);
  }, [stopRaf, tick]);

  // resumeTail から参照するための ref（宣言順の都合）
  const startRafRef = useRef(startRaf);
  startRafRef.current = startRaf;

  // ===== video 要素イベント（動画区間はイベント駆動で音を追従させる） =====

  useEffect(() => {
    const el = videoEl;
    if (!el) return;

    const displayTime = () => getVideoDisplayTime();

    const handlePlay = () => {
      const videoLen = getVideoLen();
      const total = Math.max(videoLen, getClipsEnd());

      if (modeRef.current === 'tail') {
        if (isPlayingRef.current) {
          // テール再生中に video が play された（動画クリック等）→ 一時停止として扱う
          pausedAtRef.current = getCurrentTime();
          internalVideoPauseRef.current = true;
          el.pause();
          stopSources();
          setPlaying(false);
          stopRaf();
          return;
        }
        if (pausedAtRef.current < total - EDGE_EPSILON) {
          // テール一時停止からの再開
          internalVideoPauseRef.current = true;
          el.pause();
          resumeTail(pausedAtRef.current);
          return;
        }
        // 終端からの再再生 → 先頭区間の頭へ戻して動画区間として続行
        modeRef.current = 'video';
        switchToSpan(0, 0, true);
      }

      const ctx = ensureCtx();
      if (ctx?.state === 'suspended') void ctx.resume().catch(() => {});
      setPlaying(true);
      startRafRef.current();
      // スケジュールは 'playing'（実際にフレームが進み始めた時点）で行う
    };

    const handlePlaying = () => {
      if (modeRef.current !== 'video' || !isPlayingRef.current) return;
      // 区間切替中はロード完了後（loadedmetadata → seek → playing）に改めて走る
      if (pendingSpanSwitchRef.current) return;
      scheduleAllFrom(displayTime());
    };

    const handlePause = () => {
      if (internalVideoPauseRef.current) {
        internalVideoPauseRef.current = false;
        return;
      }
      // src 差し替え（区間切替）に伴う過渡的な pause は無視する
      if (pendingSpanSwitchRef.current) return;
      if (el.ended) return; // 終端は 'ended' 側で処理（テール遷移の可能性）
      if (modeRef.current !== 'video') return;
      pausedAtRef.current = displayTime();
      stopSources();
      setPlaying(false);
      stopRaf();
    };

    const handleEnded = () => {
      if (modeRef.current !== 'video' || !isPlayingRef.current) return;
      if (pendingSpanSwitchRef.current) return;
      const videoLen = getVideoLen();
      // ファイルが区間より先に尽きた（候補動画が短い等）場合は次区間の頭へ
      const spans = segmentRef.current.videoSpans;
      const spanIdx = Math.min(spanIndexRef.current, spans.length - 1);
      const nextSpan = spans[spanIdx + 1];
      if (nextSpan && spanStartsWithinDisplay(nextSpan.startSeconds, videoLen)) {
        switchToSpan(spanIdx + 1, nextSpan.startSeconds, true);
        return;
      }
      const t = Math.min(displayTime(), videoLen);
      if (Math.max(getClipsEnd(), videoLen) > t + EDGE_EPSILON) {
        enterTail(t);
      } else {
        finishPlayback(videoLen);
      }
    };

    const handleSeeking = () => {
      if (modeRef.current !== 'video') return;
      if (isPlayingRef.current) stopSources();
    };

    const handleSeeked = () => {
      if (modeRef.current !== 'video') return;
      if (pendingSpanSwitchRef.current) return;
      pausedAtRef.current = displayTime();
      if (isPlayingRef.current && !el.paused) scheduleAllFrom(displayTime());
    };

    const handleWaiting = () => {
      // 動画バッファリング中は音を止めて先行を防ぐ（'playing' で再スケジュール）
      if (modeRef.current !== 'video') return;
      if (isPlayingRef.current) stopSources();
    };

    el.addEventListener('play', handlePlay);
    el.addEventListener('playing', handlePlaying);
    el.addEventListener('pause', handlePause);
    el.addEventListener('ended', handleEnded);
    el.addEventListener('seeking', handleSeeking);
    el.addEventListener('seeked', handleSeeked);
    el.addEventListener('waiting', handleWaiting);
    el.addEventListener('stalled', handleWaiting);

    return () => {
      el.removeEventListener('play', handlePlay);
      el.removeEventListener('playing', handlePlaying);
      el.removeEventListener('pause', handlePause);
      el.removeEventListener('ended', handleEnded);
      el.removeEventListener('seeking', handleSeeking);
      el.removeEventListener('seeked', handleSeeked);
      el.removeEventListener('waiting', handleWaiting);
      el.removeEventListener('stalled', handleWaiting);
    };
  }, [
    videoEl,
    getVideoLen,
    getVideoDisplayTime,
    getClipsEnd,
    getCurrentTime,
    ensureCtx,
    setPlaying,
    stopSources,
    stopRaf,
    resumeTail,
    scheduleAllFrom,
    switchToSpan,
    enterTail,
    finishPlayback,
  ]);

  // ===== loadedmetadata: 区間切替の位置合わせ + 初期表示の頭出し =====
  // - 区間切替（pendingSpanSwitch）中: 目標表示時刻のファイル時刻へシークし、
  //   resumePlay なら再生を再開する
  // - 通常ロード（初期マウント・再リンク等）: 現在区間のオフセットへ頭出し
  //   （ポスターではなく offset フレームから表示する）
  useEffect(() => {
    const el = videoEl;
    if (!el) return;
    const handleLoadedMetadata = () => {
      const span = getCurrentSpan();
      const pending = pendingSpanSwitchRef.current;
      if (pending) {
        pendingSpanSwitchRef.current = null;
        const fileTime = Math.max(
          0,
          span.fileOffsetSeconds + (pending.displayTime - span.startSeconds),
        );
        el.currentTime = Number.isFinite(el.duration) ? Math.min(el.duration, fileTime) : fileTime;
        if (pending.resumePlay) void el.play().catch(() => {});
        return;
      }
      if (span.fileOffsetSeconds > 0 && el.currentTime < span.fileOffsetSeconds - 0.01) {
        el.currentTime = span.fileOffsetSeconds;
      }
    };
    // 既にロード済み（マウント直後など）の場合も頭出しを適用する。
    // 切替進行中はロード済み内容が旧ファイルのため loadedmetadata を待つ
    if (el.readyState >= 1 && !pendingSpanSwitchRef.current) handleLoadedMetadata();
    el.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => el.removeEventListener('loadedmetadata', handleLoadedMetadata);
  }, [videoEl, segment.videoItem.id, getCurrentSpan]);

  // ===== 公開ハンドル =====

  const pause = useCallback(() => {
    if (!isPlayingRef.current) return;
    if (modeRef.current === 'video') {
      const el = videoElRef.current;
      if (el && !el.paused) {
        el.pause(); // 'pause' イベント経由で停止処理される
        return;
      }
    }
    pausedAtRef.current = getCurrentTime();
    stopSources();
    setPlaying(false);
    stopRaf();
  }, [getCurrentTime, stopSources, setPlaying, stopRaf]);

  const seekTo = useCallback(
    (seconds: number) => {
      const el = videoElRef.current;
      const seg = segmentRef.current;
      const videoLen = getVideoLen();
      const total = getDuration();
      const t = Math.max(0, total > 0 ? Math.min(seconds, total) : seconds);
      const wasPlaying = isPlayingRef.current;

      if (videoLen <= 0 || t < videoLen - EDGE_EPSILON) {
        // ---- 動画区間へのシーク ----
        modeRef.current = 'video';
        // t が属する区間へ切替（同一ファイルはシーク / 別ファイルは src 差し替え）。
        // 再開は switchToSpan 側: テール復帰・一時停止中は el.paused のため play される
        switchToSpan(videoSpanIndexAt(seg.videoSpans, t), t, wasPlaying);
      } else {
        // ---- テール区間へのシーク ----
        stopSources();
        modeRef.current = 'tail';
        if (el && !el.paused) {
          internalVideoPauseRef.current = true;
          el.pause();
        }
        // 動画は表示尺内の最終区間の末尾フレームで停止表示
        switchToSpan(finalSpanIndex(seg.videoSpans, videoLen), videoLen, false);
        pausedAtRef.current = t;
        if (wasPlaying) {
          resumeTail(t);
        }
      }
    },
    [getVideoLen, getDuration, stopSources, switchToSpan, resumeTail],
  );

  const play = useCallback(async () => {
    if (isPlayingRef.current) return;
    const ctx = ensureCtx();
    if (ctx?.state === 'suspended') {
      try {
        await ctx.resume();
      } catch {
        /* ユーザー操作前は resume 不可（video のみ再生される） */
      }
    }

    const el = videoElRef.current;
    const videoLen = getVideoLen();
    const total = getDuration();
    const t = pausedAtRef.current;

    // 終端で停止中 → 先頭から
    if (total > 0 && t >= total - EDGE_EPSILON) {
      seekTo(0);
      if (pendingSpanSwitchRef.current) {
        // 先頭区間の動画をロード中。完了後に再生を引き継ぐ
        pendingSpanSwitchRef.current = { ...pendingSpanSwitchRef.current, resumePlay: true };
        setPlaying(true);
        startRafRef.current();
        return;
      }
      if (el) await el.play().catch(() => {});
      return;
    }
    // テール区間で一時停止中 → テール再開（ファイルが表示尺より先に尽きて
    // 表示尺の手前でテールに入った場合も、video を頭から再生させない）
    if (modeRef.current === 'tail' || (videoLen > 0 && t >= videoLen - EDGE_EPSILON)) {
      resumeTail(t);
      return;
    }
    // 動画区間 → video 再生（'play' / 'playing' ハンドラが状態とスケジュールを担う）
    if (el) {
      modeRef.current = 'video';
      if (pendingSpanSwitchRef.current) {
        // 区間切替（src 差し替え）中。ロード完了後に再生を引き継ぐ
        pendingSpanSwitchRef.current = { ...pendingSpanSwitchRef.current, resumePlay: true };
        setPlaying(true);
        startRafRef.current();
        return;
      }
      await el.play().catch(() => {});
    }
  }, [ensureCtx, getVideoLen, getDuration, seekTo, resumeTail, setPlaying]);

  const toggle = useCallback(() => {
    if (isPlayingRef.current) {
      pause();
    } else {
      void play();
    }
  }, [pause, play]);

  const handle = useMemo<MaEngineHandle>(
    () => ({ play, pause, seekTo, toggle, getCurrentTime, getDuration }),
    [play, pause, seekTo, toggle, getCurrentTime, getDuration],
  );

  // ===== セグメント内容の変更追従（オプション候補切替・タイムライン編集） =====
  // クリップのナッジ / 削除 / トリム / 追加 / バリアント切替 / オプション候補切替で
  // セグメントの identity が変わる。表示時刻を保ったまま動画区間を再解決し
  // （候補動画の差し替え・ファイル時刻の変化を反映）、再生中なら
  // 現在位置からオーディオを再スケジュールして即時反映する
  const prevSegmentRef = useRef(segment);
  useEffect(() => {
    const prev = prevSegmentRef.current;
    if (prev === segment) return;
    prevSegmentRef.current = segment;

    const el = videoElRef.current;
    const prevSpans = prev.videoSpans;
    const prevSpan = prevSpans[Math.min(spanIndexRef.current, prevSpans.length - 1)];
    const pending = pendingSpanSwitchRef.current;

    if (modeRef.current === 'video') {
      // 表示時刻は旧セグメントのマッピングで求める（切替進行中は目標時刻）
      const t = pending
        ? pending.displayTime
        : el
          ? Math.max(0, prevSpan.startSeconds + (el.currentTime - prevSpan.fileOffsetSeconds))
          : pausedAtRef.current;
      const resume = pending ? pending.resumePlay : isPlayingRef.current && !!el && !el.paused;
      switchToSpan(
        videoSpanIndexAt(segmentRef.current.videoSpans, t),
        t,
        resume,
        prevSpan.videoItem.id,
      );
      // 区間が変わらない（シークも src 差し替えも不要の）場合もクリップ変更を反映する
      if (
        !pendingSpanSwitchRef.current &&
        isPlayingRef.current &&
        el &&
        !el.paused &&
        !el.seeking
      ) {
        scheduleAllFrom(t);
      }
      return;
    }

    // テール区間: 最終フレームの動画が変わる場合は差し替え、音は現在位置から再スケジュール
    const t = getCurrentTime();
    const videoLen = getVideoLen();
    const spans = segmentRef.current.videoSpans;
    switchToSpan(finalSpanIndex(spans, videoLen), videoLen, false, prevSpan.videoItem.id);
    pausedAtRef.current = t;
    if (isPlayingRef.current) scheduleAllFrom(t);
  }, [segment, switchToSpan, scheduleAllFrom, getCurrentTime, getVideoLen]);

  // ===== アクティブ状態の管理 =====

  // アクティブ化: クリップを先行デコード（初回再生の頭欠けを防ぐ）
  useEffect(() => {
    if (!active) return;
    segmentRef.current.clips.forEach(clip => {
      const state = buffersRef.current.get(clip.audioItem.id);
      if (!state) ensureBuffer(clip);
    });
  }, [active, segment, ensureBuffer]);

  // 非アクティブ化: 停止してデコード済みバッファを解放（メモリ対策）
  useEffect(() => {
    if (active) return;
    pause();
    generationRef.current += 1;
    buffersRef.current.clear();
  }, [active, pause]);

  // アンマウント時のクリーンアップ
  useEffect(
    () => () => {
      generationRef.current += 1;
      stopSources();
      stopRaf();
      if (masterGainRef.current) {
        try {
          masterGainRef.current.disconnect();
        } catch {
          /* noop */
        }
        masterGainRef.current = null;
      }
    },
    [stopSources, stopRaf],
  );

  // 現在表示すべき動画（現在区間の videoItem）。セグメント差し替えで
  // 区間数が減った場合に備えてクランプする
  const displayVideoItem =
    segment.videoSpans[Math.min(currentSpanIndex, segment.videoSpans.length - 1)].videoItem;

  return { handle, displayVideoItem };
}
